import React, { Suspense, useMemo } from 'react'
import type { MassPayoutsProps } from 'mass-payouts/MassPayouts'
import { RowsSkeletonLoader } from '@node-space/ui-components'
import { Approval } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useThemeContext } from 'hooks/context/useThemeContext'
import { useApprovalsConfigForFlow } from 'hooks/useApprovalsConfigForFlow'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useContactsMfeFeatureFlags } from 'pages/Contacts/hooks/useContactsMfeFeatureFlags'
import { createMFELazy } from 'utils/createMFELazy'
import track from 'utils/tracker'

const MassPayoutsLazy = createMFELazy<MassPayoutsProps>(() => import('mass-payouts/MassPayouts'), {
  mfeName: 'Mass Payouts',
  testId: 'mass-payouts-mfe-error',
})

export const MassPayoutsMFE = () => {
  const { currentAccount } = useAccountsContext() || {}
  const { darkMode } = useThemeContext()
  const contactsFeatureFlags = useContactsMfeFeatureFlags()
  const { enableUsLaunchRestrictions, enableUsRestrictedCurrencies, enableMaltaAccounts } =
    useFeatureFlags()

  const {
    approvalsIsRequiredForFlow: isCryptoPayoutApprovalRequired,
    isFetchingApprovalPreference,
  } = useApprovalsConfigForFlow(Approval.FLOWS.CRYPTO_PAYOUT)
  const { approvalsIsRequiredForFlow: isFiatPayoutApprovalRequired } = useApprovalsConfigForFlow(
    Approval.FLOWS.FIAT_3RD_PARTY_PAYOUT
  )

  const featureFlags = useMemo(
    () => ({ enableUsLaunchRestrictions, enableUsRestrictedCurrencies, enableMaltaAccounts }),
    [enableUsLaunchRestrictions, enableUsRestrictedCurrencies, enableMaltaAccounts]
  )

  const approvals = useMemo(
    () => ({
      isCryptoPayoutApprovalRequired: !!isCryptoPayoutApprovalRequired,
      isFiatPayoutApprovalRequired: !!isFiatPayoutApprovalRequired,
      isFetchingApprovalPreference: !!isFetchingApprovalPreference,
    }),
    [isCryptoPayoutApprovalRequired, isFiatPayoutApprovalRequired, isFetchingApprovalPreference]
  )

  return (
    <Suspense fallback={<RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}>
      <MassPayoutsLazy
        bffUrl={process.env.VITE_BASE_URL}
        accountReference={currentAccount?.reference}
        featureFlags={featureFlags}
        approvals={approvals}
        darkMode={darkMode}
        onTrack={track.Amp.track}
        links={{
          templatesBaseUrl: process.env.ASSETS_URL ?? '',
          contactSupportUrl: process.env.SUPPORT_REQUEST_URL ?? '',
          iconsUrl: process.env.ICONS_URL ?? '',
        }}
        contactsFeatureFlags={contactsFeatureFlags}
      />
    </Suspense>
  )
}
